import axios from 'axios';
import { AuthenticationClient } from '../authentication/client';

const LOGGING_URL = '/ui-logging/log';

type Properties = { [key: string]: string | number | Event | undefined };

enum LogLevel {
  info = 'info',
  warning = 'warning',
  error = 'error',
}

type EventPayload = {
  message: string;
  level: LogLevel;
  mfeName?: string;
  source?: string;
  componentName?: string;
  url: string;
  tags?: Properties;
  error?: Error;
};

async function sendLogEvent(event: EventPayload): Promise<void> {
  try {
    const token = await AuthenticationClient.getInstance().getAccessToken();
    if (token) {
      await axios.post(LOGGING_URL, event, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
  } catch (e) {
    const statusCode = e.response?.status;
    const condition = statusCode ? e.message : 'network error';
    // eslint-disable-next-line no-console
    console.error(`Failed to send log message to server ${event.level} - ${condition}`);
  }
}

function injectLogger(): void {
  window.onerror = (event, source, lineno, colno, err): void => {
    if (err) {
      error(err, {
        message: 'Error caught by window.onerror',
        event,
        source,
        lineno,
        colno,
      });
    }
  };

  window.onunhandledrejection = (event: PromiseRejectionEvent): void => {
    if (event) {
      error(event.reason);
    }
  };
}

async function log(level: LogLevel, message: string, properties?: Properties, error?: Error): Promise<void> {
  const event: EventPayload = {
    url: window.location.href,
    level,
    message,
    error,
  };

  if (properties) {
    const { componentName, source, mfeName, ...rest } = properties;

    if (mfeName) {
      event.mfeName = mfeName.toString();
    }

    if (source) {
      event.source = source.toString();
    }

    if (componentName) {
      event.componentName = componentName.toString();
    }

    if (Object.getOwnPropertyNames(rest).length > 0) {
      event.tags = rest;
    }
  }

  await sendLogEvent(event);
}

async function error(error: Error, properties?: Properties): Promise<void> {
  // eslint-disable-next-line no-console
  console.error(error);

  await log(LogLevel.error, error.message, properties, error);
}

async function warning(message: string, properties?: Properties): Promise<void> {
  await log(LogLevel.warning, message, properties);
}

async function info(message: string, properties?: Properties): Promise<void> {
  await log(LogLevel.info, message, properties);
}

export { injectLogger, error, warning, info, Properties, LOGGING_URL };
